import { getAddress, isAddress } from 'viem';
import { z } from 'zod';

import { ApiError } from '@/lib/api-client';
import { truncateAddress } from '@/lib/format/address';
import { netOfPendingTwin } from '@/lib/net-of-pending';
import { reportGlobalError } from '@/lib/observability';
import { reportFallback } from '@/lib/report-fallback';
import { tokenSymbolField } from '@/lib/token-symbol';
import { reportBrokenField, tolerantRows } from '@/lib/tolerant-rows';
import { baseUnitsString } from '@/lib/vaults/base-units';

/**
 * Boundary tolerance: these schemas validate and bound every field the UI
 * consumes, but deliberately STRIP unknown keys instead of rejecting them
 * (same convention as the hygiene contract). Backend releases add fields
 * additively; a new key must never turn a healthy 200 into a client-side
 * error state — 1.40.x adding `populationScope` did exactly that while the
 * response objects were strict.
 *
 * The same rule, applied to values:
 *
 * - A vocabulary (a basis, a source, a status, a code) is a bounded string,
 *   never a literal or a closed enum. A value this build has not seen is
 *   carried, or read as the vocabulary's own "unknown" where a screen keys
 *   on it.
 * - A label may be empty upstream. Empty is "no label", not a broken
 *   response.
 * - A cross-field invariant that fails becomes a `warnings[]` entry, or
 *   drops the one derived block it invalidates. The response is still
 *   served. Still refused today: a correlation matrix that is not square
 *   and symmetric, and a liquidity horizon out of its fixed bucket order.
 * - The request scope (owner address, chain, window echo) stays strict:
 *   that is what stops one wallet's figures painting under another's.
 */
const finiteNumber = z.number().finite();
const nonNegativeInteger = z.number().int().nonnegative();
/** A true share of a whole: 0% to 100%. */
const basisPoints = z.number().int().min(0).max(10_000);
/**
 * A weight of the portfolio. Gross exposure can exceed 100% (a leveraged
 * vault holds more than its equity), so this is bounded for sanity, not at
 * 10000. Concentration measures derived from these weights share the bound.
 */
const exposureWeightBps = z.number().int().min(0).max(1_000_000);
const signedBasisPoints = z.number().int().finite();
const isoDateTime = z.iso.datetime({ offset: true });
const nullableIsoDateTime = isoDateTime.nullable();
/** Text the screen prints or keys on. May be empty; bounded in length. */
const boundedText = z.string().trim().max(512);
/** A label: empty upstream is the same as absent. */
const optionalBoundedText = z
  .string()
  .trim()
  .max(512)
  .nullable()
  .transform((value) => (value === '' ? null : value));
/** An open vocabulary: carried as served, bounded in length. */
const vocabulary = z.string().max(128);

/**
 * Monetary values deliberately stay as decimal strings at the API boundary.
 * Converting them to binary floating point here would silently lose precision.
 */
const decimalStringSchema = z
  .string()
  .regex(
    /^(?:0|[1-9]\d*)(?:\.\d+)?$/,
    'Expected a non-negative decimal string'
  );

const evmAddressSchema = z
  .string()
  .refine(isAddress, 'Expected a valid EVM address');

const investorScopeSchema = z.object({
  ownerAddress: evmAddressSchema,
  chainId: z.number().int().positive()
});

/** The most served warning text kept: it is never shown, only read. */
const SERVED_MESSAGE_MAX = 1024;

/**
 * A warning's served text, which no screen renders (screens speak from the
 * code). Bounded by cutting, never by rejecting: one long backend sentence
 * must not fail the read it rides on. Only for text that is never shown;
 * shown text is never cut mid word.
 */
export const servedMessageSchema = z
  .string()
  .transform((message) => message.slice(0, SERVED_MESSAGE_MAX));

/** Normalized-ledger warnings served on `/risk` (code vocabulary can grow). */
const ledgerWarningSchema = z.object({
  code: z.string().max(128),
  message: servedMessageSchema
});

export const analyticsWindowSchema = z.enum([
  '7d',
  '1m',
  '3m',
  '6m',
  '1y',
  'ytd',
  'all',
  '30d',
  '90d',
  '180d',
  '365d',
  '12m',
  'inception'
]);

export type AnalyticsWindow = z.infer<typeof analyticsWindowSchema>;

export function normalizeAnalyticsAddress(address: string): string {
  if (!isAddress(address)) {
    throw new ApiError('The connected wallet address is invalid.', {
      status: 400,
      code: 'invalid-request'
    });
  }

  return getAddress(address).toLowerCase();
}

export function normalizeAnalyticsChainId(chainId: number): number {
  if (!Number.isInteger(chainId) || chainId <= 0) {
    throw new ApiError('The selected network is invalid.', {
      status: 400,
      code: 'invalid-request'
    });
  }

  return chainId;
}

export function assertAnalyticsResponseScope(
  requestedAddress: string,
  requestedChainId: number,
  response: { ownerAddress: string; chainId: number }
): void {
  if (
    response.ownerAddress.toLowerCase() !== requestedAddress.toLowerCase() ||
    response.chainId !== requestedChainId
  ) {
    throw new ApiError('ROWA returned data that could not be verified.', {
      status: 200,
      code: 'invalid-response'
    });
  }
}

const historySchema = z.object({
  availableDays: nonNegativeInteger,
  requiredDaysSharpe: nonNegativeInteger,
  requiredDaysMaxDd: nonNegativeInteger,
  /**
   * v1.59.0 — the risk-ratio gate is on the REQUESTED window, not account
   * age: the ratios are null exactly when
   * `usableReturnsInWindow < requiredUsableReturns`. Optional so a 1.58.0
   * payload still parses.
   */
  usableReturnsInWindow: nonNegativeInteger.optional(),
  requiredUsableReturns: nonNegativeInteger.optional()
});

/** API 1.159.0: the /metrics window return twin, passed through. */
const windowReturnNetOfPending = netOfPendingTwin(
  'investor risk metrics',
  {
    value: 'windowReturnNetOfPendingBps',
    reason: 'windowReturnNetOfPendingReason'
  },
  { value: signedBasisPoints }
);

const riskMetricsSchema = z
  .object({
    volatilityBps: nonNegativeInteger.nullable(),
    sharpeBps: signedBasisPoints.nullable(),
    sortinoBps: signedBasisPoints.nullable(),
    /** Positive loss magnitude: 480 means a 4.80% maximum drawdown. */
    maxDrawdown12mBps: basisPoints.nullable(),
    windowReturnBps: signedBasisPoints.nullable(),
    ytdPerformanceBps: signedBasisPoints.nullable(),
    ...windowReturnNetOfPending.shape
  })
  .transform(windowReturnNetOfPending.read);

const vaultExposureSchema = z
  .object({
    vaultAddress: evmAddressSchema,
    name: boundedText.nullish(),
    valueUsd: decimalStringSchema.nullable(),
    weightBps: exposureWeightBps.nullable()
  })
  // A vault can be unnamed upstream. Its row still needs something to
  // print, and its own short address is the one true thing available.
  .transform((vault) => ({
    ...vault,
    name: vault.name || truncateAddress(vault.vaultAddress)
  }));

/**
 * v1.52.0 — the MEASURED per-vault attribution of an asset's value.
 * `weightBps` is the share of THE ASSET's value (≈10000 across entries),
 * a different denominator from the asset row's own `weightBps`.
 */
const assetVaultWeightSchema = z.object({
  vaultAddress: evmAddressSchema,
  valueUsd: decimalStringSchema,
  weightBps: basisPoints
});

/** Cents for the exact-sum identity; null when not a 2dp decimal string. */
const usdCentsOrNull = (value: string): bigint | null => {
  const match = /^(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  if (!match) return null;
  return BigInt(match[1]!) * 100n + BigInt((match[2] ?? '').padEnd(2, '0'));
};

/**
 * v1.153.0 — `valueUsd`/`weightBps` are the GROSS long holding: what the
 * vaults hold, including collateral posted on a credit line. Never
 * negative. Under borrowing the rows sum to `grossExposureBps`, past
 * `allocatedBps`, and one row can exceed 10000. What is owed is in
 * `liabilities`, never netted in here.
 */
const assetExposureSchema = z.object({
  address: evmAddressSchema,
  symbol: optionalBoundedText,
  /** The token's own `symbol()` (v1.160.0); `symbol` is the registry code. */
  tokenSymbol: tokenSymbolField(
    'investor-risk',
    'investor.risk.byAsset.tokenSymbol'
  ),
  name: optionalBoundedText,
  valueUsd: decimalStringSchema,
  weightBps: exposureWeightBps,
  /** Superseded by viaVaultWeights (v1.52.0); kept for compatibility. */
  viaVaults: z.array(evmAddressSchema).max(100),
  // v1.52.0, additive — optional so pre-1.52 payloads still parse.
  viaVaultWeights: z.array(assetVaultWeightSchema).max(100).optional(),
  /** true iff the effective class is stablecoins; false = not classified
   *  as one, NOT proven otherwise (classifiedWeightBps is the coverage). */
  stablecoin: z.boolean().optional()
});

/**
 * Whether an asset's measured splits add up to its value, in integer cents
 * (never float math). Each contribution is rounded to cents on its own, so
 * the sum drifts by up to a cent per entry: an asset split three ways lands
 * on 99.99 where the asset says 100.00. Demanding exactness here took the
 * entire Risk and Liquidity screen down over one cent, and refusing the
 * response over a real attribution error took it down as well; now the
 * splits of that one asset are dropped (see `degradeRiskInvariants`).
 */
function splitsAddUp(asset: {
  valueUsd: string;
  viaVaultWeights?: readonly { valueUsd: string }[];
}): boolean {
  if (asset.viaVaultWeights == null) return true;
  const assetCents = usdCentsOrNull(asset.valueUsd);
  const splitCents = asset.viaVaultWeights.map((entry) =>
    usdCentsOrNull(entry.valueUsd)
  );
  if (assetCents == null || !splitCents.every((cents) => cents != null)) {
    return true;
  }
  const sum = splitCents.reduce<bigint>((total, cents) => total + cents!, 0n);
  const tolerance = BigInt(asset.viaVaultWeights.length);
  const drift = sum > assetCents ? sum - assetCents : assetCents - sum;
  return drift <= tolerance;
}

/**
 * Whether an asset's measured splits name exactly the vaults it is routed
 * through. When they do not, the splits are not trusted as an attribution
 * and are dropped for that asset (see `degradeRiskInvariants`); the asset
 * row itself, and `viaVaults`, are still served.
 */
function splitsCoverRoutes(asset: {
  viaVaults: readonly string[];
  viaVaultWeights?: readonly { vaultAddress: string }[];
}): boolean {
  if (asset.viaVaultWeights == null) return true;
  const splitAddresses = new Set(
    asset.viaVaultWeights.map((entry) => entry.vaultAddress.toLowerCase())
  );
  const routeAddresses = new Set(
    asset.viaVaults.map((address) => address.toLowerCase())
  );
  return (
    splitAddresses.size === routeAddresses.size &&
    [...splitAddresses].every((address) => routeAddresses.has(address))
  );
}

/**
 * v1.153.0 — what the vaults OWE on credit lines, accrued interest
 * included. `weightBps` shares the byAsset denominator (the priced position
 * total, net of the debt). An asset that is only owed appears here and not
 * in byAsset. The served per vault split (`viaVaultWeights`) is not read:
 * nothing shows it, and a field nobody reads must not void the debt list.
 */
const liabilitySchema = z.object({
  address: evmAddressSchema,
  symbol: optionalBoundedText.optional(),
  /** The token's own `symbol()` (v1.160.0); `symbol` is the registry code. */
  tokenSymbol: tokenSymbolField(
    'investor-risk',
    'investor.risk.liabilities.tokenSymbol'
  ),
  name: optionalBoundedText.optional(),
  valueUsd: decimalStringSchema,
  weightBps: exposureWeightBps,
  viaVaults: z.array(evmAddressSchema).max(100)
});

type InvestorLiability = z.infer<typeof liabilitySchema>;

const exposureBucketSchema = z.object({
  slug: boundedText,
  displayName: optionalBoundedText,
  valueUsd: decimalStringSchema,
  weightBps: exposureWeightBps
});

const chainExposureSchema = z.object({
  chainId: z.number().int().positive(),
  valueUsd: decimalStringSchema,
  weightBps: exposureWeightBps
});

const exposuresSchema = z.object({
  byVault: z.array(vaultExposureSchema).max(100),
  byAsset: z.array(assetExposureSchema).max(1_000),
  byAssetClass: z.array(exposureBucketSchema).max(1_000),
  byIssuer: z.array(exposureBucketSchema).max(1_000),
  byChain: z.array(chainExposureSchema).max(100),
  /**
   * api 1.44.0, additive — optional so pre-1.44 backends still parse.
   * Same bucket shape, two contract differences from byIssuer:
   * `slug` is a lowercase registry EOA (or the residual 'unknown'), not a
   * taxonomy key; and `weightBps` is bps of the ATTRIBUTED value (sums to
   * ~10000), not of the priced-position total behind byAssetClass/byIssuer
   * — never validate these weights against exposureCoverage.allocatedBps.
   */
  byStrategist: z.array(exposureBucketSchema).max(1_000).optional(),
  /**
   * v1.153.0, `[]` when nothing is owed. Absent from older backends, which
   * is not a reading of "no debt" either: byAsset then is what it always
   * was. All or nothing: a debt list missing a row would understate what
   * is owed, so one unreadable row (or a block that is not a list) makes
   * the whole block unreadable, `null`, reported once as a client warning
   * (see `degradeRiskInvariants`). Never the whole response.
   */
  liabilities: z
    .array(liabilitySchema)
    .max(1_000)
    .nullable()
    .optional()
    .catch(null)
});

/**
 * Coverage counts and weights.
 *
 * Counts that cannot be true (malformed, past the 100 position ceiling,
 * more priced positions than positions, or no positions beside any
 * exposure row) make this block unreadable: it is served as `null` and every
 * figure that depends on it is withheld as "could not be verified". Never
 * the whole response, which feeds Risk, Portfolio and Overview at once,
 * and never a reading of "no positions" from counts that contradict
 * themselves. Weights above allocation (classified, known issuer) can be
 * real under gross exposure, so those stay a warning (see
 * `degradeRiskInvariants`).
 */
const exposureCoverageSchema = z.object({
  positionsTotal: nonNegativeInteger.max(100),
  pricedCount: nonNegativeInteger.max(100),
  // Null = unmeasurable, not zero (API v1.57.0).
  pricedBps: basisPoints.nullable(),
  positionsTruncated: z.boolean(),
  /** v1.153.0: NET of liabilities, ≈ `netExposureBps`. */
  allocatedBps: exposureWeightBps,
  // Measured over the gross held rows (v1.153.0), so under borrowing these
  // can pass allocatedBps by the debt.
  classifiedWeightBps: exposureWeightBps,
  issuerKnownBps: exposureWeightBps,
  /**
   * v1.153.0, additive. The share of the priced total read from RECORDED
   * holdings (not target weights or the strategy currency), and the oldest
   * holdings read used (null when none was). Read tolerantly: a malformed
   * value loses only itself, never the coverage counts every screen gates on.
   */
  recordedHoldingsBps: basisPoints.optional().catch(undefined),
  holdingsAsOf: nullableIsoDateTime.optional().catch(undefined)
});

/**
 * v1.89.0 — null is a THIRD state: "exposure values were not all
 * measurable". The whole block goes null together, topAsset included, and
 * it is NOT zero. An all-ZEROS block is a different reachable state — "no
 * priced exposure" (e.g. a lone 0-share position) — and is also not a
 * measurement of concentration; `topAsset: null` is the tell there, and
 * `exposureCoverage.pricedCount`/`pricedBps` distinguish the two. No
 * cross-field refinement rejecting a partially-null block on purpose —
 * the tolerance policy at the top of this file applies.
 */
const concentrationSchema = z.object({
  topAsset: z
    .object({
      address: evmAddressSchema,
      symbol: optionalBoundedText,
      /** The token's own `symbol()` (v1.160.0). */
      tokenSymbol: tokenSymbolField(
        'investor-risk',
        'investor.risk.topAsset.tokenSymbol'
      ),
      weightBps: exposureWeightBps
    })
    .nullable(),
  top1WeightBps: exposureWeightBps.nullable(),
  top5WeightBps: exposureWeightBps.nullable(),
  hhiBps: exposureWeightBps.nullable(),
  diversificationScore: finiteNumber.min(0).max(100).nullable()
});

/** Same v1.89.0 null semantics as the concentration block above. */
const vaultConcentrationSchema = z.object({
  top1WeightBps: exposureWeightBps.nullable(),
  hhiBps: exposureWeightBps.nullable()
});

/**
 * v1.54.0 — the vault-level matrix is served; `basis` states what it IS
 * computed on and `plannedBasis` narrows to the unshipped asset level.
 * The false arm keeps pre-1.54 payloads parsing.
 */
const correlationStatusSchema = z.union([
  z.object({
    available: z.literal(true),
    reason: boundedText,
    basis: z.string().max(4000),
    plannedBasis: z.string().max(4000)
  }),
  z.object({
    available: z.literal(false),
    reason: boundedText,
    plannedBasis: z.string().max(4000)
  })
]);

const correlationBps = z.number().int().min(-10_000).max(10_000);

const correlationMatrixSchema = z
  .object({
    /**
     * Held vaults — row AND column order. Served address-ascending; an
     * unsorted list still lines up with its own rows and columns, so it is
     * a warning rather than a refusal (see `degradeRiskInvariants`).
     */
    vaults: z.array(evmAddressSchema).max(100),
    matrix: z.array(z.array(correlationBps.nullable()).max(100)).max(100),
    observations: z.array(z.array(nonNegativeInteger).max(100)).max(100),
    minOverlapDays: nonNegativeInteger,
    /**
     * v1.62.0 — the matrix is capped at 60 held vaults. `heldCount` is the
     * deduped held-vault count BEFORE that cap, so it equals `vaults.length`
     * unless `truncated`. When truncated the matrix covers the first 60 by
     * ascending address: deterministic, and deliberately NOT the 60 largest,
     * so the omitted vaults are not the small ones. Presenting that as a
     * complete picture would be the lie.
     *
     * Optional so a pre-1.62.0 payload still parses.
     */
    heldCount: nonNegativeInteger.optional(),
    truncated: z.boolean().optional(),
    /**
     * Whether `vaults` is in the documented selection order (ascending by
     * address). Derived here from the served list, never read from the
     * response: copy may say the capped subset was "picked by wallet
     * address" only when this is true.
     */
    addressAscending: z.boolean().optional(),
    from: z.iso.date().nullable(),
    to: z.iso.date().nullable()
  })
  .superRefine((value, context) => {
    const size = value.vaults.length;
    for (const [key, grid] of [
      ['matrix', value.matrix],
      ['observations', value.observations]
    ] as const) {
      if (grid.length !== size || grid.some((row) => row.length !== size)) {
        context.addIssue({
          code: 'custom',
          path: [key],
          message: `${key} must be ${size}×${size}`
        });
        return;
      }
    }
    for (let row = 0; row < size; row += 1) {
      for (let col = row + 1; col < size; col += 1) {
        if (
          value.matrix[row]![col] !== value.matrix[col]![row] ||
          value.observations[row]![col] !== value.observations[col]![row]
        ) {
          context.addIssue({
            code: 'custom',
            path: ['matrix', row, col],
            message: 'Correlation and observations must be symmetric'
          });
        }
      }
    }
  });

/**
 * Mandates are not served yet (`mandates: null`, `available: false`). Both
 * are read tolerantly so that the release which ships them does not reject
 * every `/risk` response: the block is carried opaque until a screen reads
 * it, and a status this build cannot read is treated as absent.
 */
const mandatesStatusSchema = z
  .object({
    available: z.boolean(),
    reason: boundedText.catch('')
  })
  .nullish()
  .catch(null);

const riskProvenanceSchema = z.object({
  metricId: boundedText,
  formulaVersion: boundedText,
  windowDays: nonNegativeInteger.optional()
});

const servedInvestorRiskSchema = investorScopeSchema.extend({
  asOf: isoDateTime,
  // Added in backend 1.40.x; optional so pre-1.40 payloads still parse.
  populationScope: z.string().max(64).optional(),
  totalPositions: nonNegativeInteger.optional(),
  warnings: z.array(ledgerWarningSchema).max(100).optional(),
  window: analyticsWindowSchema,
  windowFrom: nullableIsoDateTime,
  windowTo: nullableIsoDateTime,
  windowDays: nonNegativeInteger,
  metrics: riskMetricsSchema,
  history: historySchema,
  exposures: exposuresSchema,
  /**
   * v1.153.0, additive. Gross = Σ byAsset weights (what the vaults hold);
   * net = gross − Σ liabilities weights ≈ `allocatedBps`. Absent before
   * 1.153.0; a malformed value reads as absent, never as zero.
   */
  grossExposureBps: exposureWeightBps.optional().catch(undefined),
  netExposureBps: exposureWeightBps.optional().catch(undefined),
  exposureCoverage: exposureCoverageSchema.nullable().catch(null),
  concentration: concentrationSchema,
  vaultConcentration: vaultConcentrationSchema,
  correlationMatrix: correlationMatrixSchema.nullable(),
  correlationStatus: correlationStatusSchema,
  mandates: z.unknown().nullable(),
  mandatesStatus: mandatesStatusSchema,
  provenance: z.array(riskProvenanceSchema).max(100)
});

type ServedInvestorRisk = z.infer<typeof servedInvestorRiskSchema>;

/** Codes for the warnings this boundary adds. Served codes never start so. */
const CLIENT_RISK_WARNING = {
  coverage: 'CLIENT_COVERAGE_INCONSISTENT',
  coverageUnreadable: 'CLIENT_COVERAGE_UNREADABLE',
  correlationOrder: 'CLIENT_CORRELATION_ORDER',
  correlationDiagonal: 'CLIENT_CORRELATION_DIAGONAL',
  correlationDuplicate: 'CLIENT_CORRELATION_DUPLICATE',
  vaultSplits: 'CLIENT_VAULT_SPLITS_DROPPED',
  liabilitiesUnreadable: 'CLIENT_LIABILITIES_UNREADABLE'
} as const;

/**
 * No screen renders `risk.warnings`, so a client warning that stayed there
 * would be invisible. Each code reaches the error tracker once per session,
 * code only: never the wallet, never the figures.
 */
const reportedRiskWarnings = new Set<string>();

function reportRiskWarning(code: string): void {
  if (reportedRiskWarnings.has(code)) return;
  reportedRiskWarnings.add(code);
  void reportGlobalError(new Error('Investor risk invariant failed'), {
    scope: 'investor-risk-invariant',
    code
  });
}

/**
 * The invariants that are worth knowing about and not worth losing the
 * screen over. Each one that fails adds a warning after the served ones;
 * figures pass through as sent. The one exception drops a derived block:
 * vault splits that do not name the asset's own vaults are removed from that
 * asset, because a consumer would otherwise attribute value to the wrong
 * vault.
 */
function degradeRiskInvariants(risk: ServedInvestorRisk): ServedInvestorRisk {
  const added: { code: string; message: string }[] = [];
  const servedCoverage = risk.exposureCoverage;
  // The backend builds every exposure collection from priced positions, so
  // with none of them each collection is empty; a row in any of them says
  // the zero count is wrong.
  const exposures = risk.exposures;
  const servesExposure = [
    exposures.byVault,
    exposures.byAsset,
    exposures.byAssetClass,
    exposures.byIssuer,
    exposures.byChain,
    exposures.byStrategist ?? []
  ].some((rows) => rows.length > 0);
  const countsContradict =
    servedCoverage != null &&
    (servedCoverage.pricedCount > servedCoverage.positionsTotal ||
      (servedCoverage.positionsTotal === 0 && servesExposure));
  const exposureCoverage = countsContradict ? null : servedCoverage;

  if (exposureCoverage == null) {
    added.push({
      code: CLIENT_RISK_WARNING.coverageUnreadable,
      message: 'Coverage counts could not be verified'
    });
  } else {
    // Classified and known-issuer weights are measured over what is held
    // (v1.153.0), which under borrowing passes the net allocation by the
    // debt. Above what is held is the inconsistency. What is held is the
    // smaller of the served gross and the held rows' own sum, so neither
    // figure alone can widen the ceiling.
    const heldRowsBps = risk.exposures.byAsset.reduce(
      (sum, asset) => sum + asset.weightBps,
      0
    );
    const heldBps = Math.max(
      exposureCoverage.allocatedBps,
      risk.grossExposureBps == null
        ? 0
        : Math.min(risk.grossExposureBps, heldRowsBps)
    );
    if (exposureCoverage.classifiedWeightBps > heldBps) {
      added.push({
        code: CLIENT_RISK_WARNING.coverage,
        message: 'Classified exposure exceeds allocated exposure'
      });
    }
    if (exposureCoverage.issuerKnownBps > heldBps) {
      added.push({
        code: CLIENT_RISK_WARNING.coverage,
        message: 'Known-issuer exposure exceeds allocated exposure'
      });
    }
  }

  const vaults = risk.correlationMatrix?.vaults ?? [];
  const ascending = vaults.every(
    (address, index) =>
      index === 0 || vaults[index - 1]!.toLowerCase() < address.toLowerCase()
  );
  if (!ascending) {
    added.push({
      code: CLIENT_RISK_WARNING.correlationOrder,
      message: 'Correlation matrix vaults are not address ascending'
    });
  }

  // The diagonal is an identity, not an estimate: a matrix that gets it
  // wrong is not trusted at all, and the screen shows the matrix as not
  // available rather than drawing it.
  let correlationMatrix =
    risk.correlationMatrix == null
      ? null
      : { ...risk.correlationMatrix, addressAscending: ascending };
  if (
    correlationMatrix != null &&
    correlationMatrix.matrix.some((row, index) => row[index] !== 10_000)
  ) {
    correlationMatrix = null;
    added.push({
      code: CLIENT_RISK_WARNING.correlationDiagonal,
      message: 'Correlation matrix dropped: its diagonal is not 10000'
    });
  }
  // Unsorted is tolerated; a vault listed twice is not. Its repeat would be
  // read as a distinct pair (its self correlation counted in the weighted
  // average) and drawn as a second row.
  if (
    correlationMatrix != null &&
    new Set(vaults.map((address) => address.toLowerCase())).size !==
      vaults.length
  ) {
    correlationMatrix = null;
    added.push({
      code: CLIENT_RISK_WARNING.correlationDuplicate,
      message: 'Correlation matrix dropped: a vault is listed more than once'
    });
  }

  let droppedSplits = 0;
  const byAsset = risk.exposures.byAsset.map((asset) => {
    if (splitsCoverRoutes(asset) && splitsAddUp(asset)) return asset;
    droppedSplits += 1;
    return { ...asset, viaVaultWeights: undefined };
  });
  if (droppedSplits > 0) {
    added.push({
      code: CLIENT_RISK_WARNING.vaultSplits,
      message: `Vault splits dropped for ${droppedSplits} asset(s): they do not cover the asset's vaults or do not add up to its value`
    });
  }

  const liabilities = risk.exposures.liabilities;
  if (liabilities === null) {
    added.push({
      code: CLIENT_RISK_WARNING.liabilitiesUnreadable,
      message: 'Borrowing in the vaults could not be read'
    });
  }

  if (added.length === 0) return { ...risk, correlationMatrix };
  for (const warning of added) reportRiskWarning(warning.code);
  return {
    ...risk,
    exposures: { ...risk.exposures, byAsset },
    exposureCoverage,
    correlationMatrix,
    warnings: [...(risk.warnings ?? []), ...added]
  };
}

export const investorRiskResponseSchema = servedInvestorRiskSchema.transform(
  degradeRiskInvariants
);

export type InvestorRisk = z.infer<typeof investorRiskResponseSchema>;

/**
 * "No ROWA positions" only when the counts consistently say zero. Coverage
 * that could not be verified is not zero: the screen says it could not be
 * verified instead.
 */
export function holdsNoRowaPositions(
  risk: Pick<InvestorRisk, 'exposureCoverage'>
): boolean {
  const coverage = risk.exposureCoverage;
  return (
    coverage != null &&
    coverage.positionsTotal === 0 &&
    coverage.pricedCount === 0 &&
    !coverage.positionsTruncated
  );
}

/** Borrowing in the vaults, as one screen reads it. */
interface ExposureLeverage {
  /**
   * Debt is proven: a debt row is served, or the served gross exposure is
   * above the net. Only then are held weights read as gross, and only then
   * does a screen say the vaults borrow.
   */
  borrowing: boolean;
  /** What is owed, largest first. Null when it could not be read. */
  liabilities: InvestorLiability[] | null;
  /**
   * Served gross and net exposure, of the priced total. Both null unless
   * both are served and agree with each other and with the debt rows (see
   * `consistentTotals`): a screen never prints a pair that contradicts
   * itself, such as a negative amount owed.
   */
  grossBps: number | null;
  netBps: number | null;
  /**
   * The share of the balance not traced to any asset (10000 − net), only
   * when the served attribution says the same (`allocatedBps`): the
   * exposure donut prints that figure as "N% attributed", and the two must
   * never disagree. Null when there are no totals or they do not agree.
   */
  untracedBps: number | null;
  /** Σ byAsset weights: everything held. THE denominator of shares of it. */
  heldTotalBps: number;
}

/**
 * The served gross and net exposure, when they can be printed together.
 * Net is gross less what is owed, so gross below net is a contradiction
 * (it would print a negative debt). Gross is Σ byAsset weights, the total
 * every share of what is held is taken of (`heldTotalBps`), so it has to
 * match the held rows, or the panel would state one total and the shares
 * use another. With readable debt rows the gap has to match their sum.
 * The backend sums the same integer weights, so a basis point per row of
 * slack is generous. Null otherwise: the screen then leaves the totals out
 * and still lists the debt rows.
 */
function consistentTotals(
  grossBps: number | undefined,
  netBps: number | undefined,
  heldRows: readonly { weightBps: number }[],
  liabilities: readonly InvestorLiability[] | null | undefined
): { grossBps: number; netBps: number } | null {
  if (grossBps == null || netBps == null || grossBps < netBps) return null;
  const heldBps = heldRows.reduce((sum, row) => sum + row.weightBps, 0);
  if (Math.abs(grossBps - heldBps) > Math.max(1, heldRows.length)) {
    return null;
  }
  if (liabilities != null) {
    const owedBps = liabilities.reduce((sum, debt) => sum + debt.weightBps, 0);
    const slack = Math.max(1, liabilities.length);
    if (Math.abs(grossBps - netBps - owedBps) > slack) return null;
  }
  return { grossBps, netBps };
}

/**
 * What is not traced to an asset, as a share of the balance. Net is held
 * less owed, ≈ `allocatedBps` (the backend's ±1 bps per row of rounding):
 * both say how much of the balance is traced. When they disagree past that
 * rounding, neither untraced figure is printed.
 */
function untracedShare(
  netBps: number | undefined,
  allocatedBps: number | undefined,
  rows: number
): number | null {
  if (netBps == null || allocatedBps == null) return null;
  if (Math.abs(netBps - allocatedBps) > Math.max(1, rows)) return null;
  return Math.max(0, 10_000 - netBps);
}

/**
 * Borrowing in the vaults (api 1.153.0). Null when there is nothing to
 * say: the backend predates the debt block, or it says nothing is owed.
 * byAsset then holds what it always held, and the screen is unchanged.
 * Otherwise either debt is proven (`borrowing`), or the debt list could
 * not be read and nothing proves debt either: then the screen says only
 * that it could not tell, and reads every share as served.
 */
export function exposureLeverage(
  risk: Pick<
    InvestorRisk,
    'exposures' | 'grossExposureBps' | 'netExposureBps' | 'exposureCoverage'
  >
): ExposureLeverage | null {
  const liabilities = risk.exposures.liabilities;
  const heldRows = risk.exposures.byAsset;
  const totals = consistentTotals(
    risk.grossExposureBps,
    risk.netExposureBps,
    heldRows,
    liabilities
  );
  const borrowing =
    (liabilities?.length ?? 0) > 0 ||
    (totals != null && totals.grossBps > totals.netBps);
  if (!borrowing && liabilities !== null) return null;
  return {
    borrowing,
    liabilities: liabilities ?? null,
    grossBps: totals?.grossBps ?? null,
    netBps: totals?.netBps ?? null,
    untracedBps: untracedShare(
      totals?.netBps,
      risk.exposureCoverage?.allocatedBps,
      heldRows.length + (liabilities?.length ?? 0)
    ),
    heldTotalBps: heldRows.reduce((sum, asset) => sum + asset.weightBps, 0)
  };
}

/**
 * What the served `concentration` block (top asset, top 1 and 5, HHI) is a
 * share of while the vaults borrow. ASSUMPTION, pending the backend's
 * answer (asked 2026-10-04): it is computed over what is held, so it is
 * already a share of Σ gross and is read as served. If the backend says it
 * is a share of the balance instead, flip this one value: every share of
 * what is held on the Risk screen goes through the two readers below.
 */
const SERVED_CONCENTRATION_BASIS = 'share-of-held' as
  | 'share-of-held'
  | 'share-of-balance';

/**
 * A held weight (byAsset, byAssetClass, byIssuer: shares of the balance)
 * as a share of everything held, so shares sum to 100% while the vaults
 * borrow. Without proven debt, as served. Null with nothing held.
 */
export function heldShareBps(
  weightBps: number,
  leverage: ExposureLeverage | null
): number | null {
  if (!leverage?.borrowing) return weightBps;
  return leverage.heldTotalBps > 0
    ? (weightBps * 10_000) / leverage.heldTotalBps
    : null;
}

/**
 * A served concentration figure on the same basis as `heldShareBps`.
 * `squared` for the HHI, a sum of squared shares.
 */
export function concentrationShareBps(
  servedBps: number | null,
  leverage: ExposureLeverage | null,
  { squared = false }: { squared?: boolean } = {}
): number | null {
  if (servedBps == null || SERVED_CONCENTRATION_BASIS === 'share-of-held') {
    return servedBps;
  }
  if (!leverage?.borrowing) return servedBps;
  if (leverage.heldTotalBps <= 0) return null;
  const scale = 10_000 / leverage.heldTotalBps;
  return servedBps * (squared ? scale * scale : scale);
}

const liquidityBoundSchema = z.object({
  cumulativeValueUsd: decimalStringSchema,
  cumulativeBps: basisPoints
});

const liquidityBucketKeys = ['t0', 't1', 't7', 't30', 't30plus'] as const;
const liquidityThresholds = [0, 1, 7, 30, null] as const;

const liquidityHorizonSchema = z
  .array(
    z.object({
      bucket: z.enum(liquidityBucketKeys),
      thresholdDays: nonNegativeInteger.nullable(),
      typical: liquidityBoundSchema,
      worstCase: liquidityBoundSchema
    })
  )
  .length(5)
  .superRefine((horizon, context) => {
    horizon.forEach((entry, index) => {
      if (entry.bucket !== liquidityBucketKeys[index]) {
        context.addIssue({
          code: 'custom',
          path: [index, 'bucket'],
          message: 'Liquidity buckets must use the canonical cumulative order'
        });
      }
      if (entry.thresholdDays !== liquidityThresholds[index]) {
        context.addIssue({
          code: 'custom',
          path: [index, 'thresholdDays'],
          message: 'Liquidity bucket threshold does not match its contract'
        });
      }
      if (entry.worstCase.cumulativeBps > entry.typical.cumulativeBps) {
        context.addIssue({
          code: 'custom',
          path: [index, 'worstCase', 'cumulativeBps'],
          message: 'Worst-case availability cannot exceed typical availability'
        });
      }
      if (index > 0) {
        const previous = horizon[index - 1]!;
        if (entry.typical.cumulativeBps < previous.typical.cumulativeBps) {
          context.addIssue({
            code: 'custom',
            path: [index, 'typical', 'cumulativeBps'],
            message: 'Typical cumulative availability must be monotone'
          });
        }
        if (entry.worstCase.cumulativeBps < previous.worstCase.cumulativeBps) {
          context.addIssue({
            code: 'custom',
            path: [index, 'worstCase', 'cumulativeBps'],
            message: 'Worst-case cumulative availability must be monotone'
          });
        }
      }
    });
  });

const liquidityAssetSchema = z.object({
  address: evmAddressSchema,
  symbol: optionalBoundedText,
  /** The token's own `symbol()` (v1.160.0); `symbol` is the registry code. */
  tokenSymbol: tokenSymbolField(
    'investor-liquidity',
    'investor.liquidity.positions.assets.tokenSymbol'
  ),
  weightBps: exposureWeightBps,
  valueUsd: decimalStringSchema,
  instantRedeemable: z.boolean(),
  typicalDays: nonNegativeInteger.nullable(),
  worstCaseDays: nonNegativeInteger.nullable(),
  exitPath: optionalBoundedText,
  // 'withdrawal-profile' | 'liquidity-estimate' today. Compare, never
  // switch exhaustively: a new source reads as "source not stated".
  source: vocabulary.nullable()
});

const LIQUIDITY_POSITION_STATUSES = ['covered', 'partial', 'unknown'] as const;
type LiquidityPositionStatus = (typeof LIQUIDITY_POSITION_STATUSES)[number];

/**
 * The positions table keys a badge on this, so the output stays the three
 * statuses it can draw. A status this build has not seen is exactly what
 * `unknown` already says: exit coverage is not established.
 */
const liquidityPositionStatus = vocabulary.transform(
  (status): LiquidityPositionStatus =>
    (LIQUIDITY_POSITION_STATUSES as readonly string[]).includes(status)
      ? (status as LiquidityPositionStatus)
      : 'unknown'
);

/** A uint256 in base units, read as a bigint (it is never float math). */
const baseUnitsSchema = baseUnitsString.transform((value) => BigInt(value));

/**
 * A new field (API 1.155.0) the app ships ahead of: absent on 1.152, so
 * optional, and a value that does not read is reported once under its
 * contract and read as `unread`, never as a reason to refuse the response.
 */
const servedField = (field: string) => `investor.liquidity.positions.${field}`;
const unreadAs = <T>(field: string, fallback: T) =>
  reportFallback('investor-analytics', servedField(field), fallback);

/**
 * What the vault itself would pay this wallet out now (its `maxWithdraw`),
 * as the backend read it. `valueUsd` null always comes with a reason
 * ('rpc_unavailable', 'unpriced', ... an open vocabulary).
 */
const servedWithdrawableSchema = z.object({
  maxWithdrawRaw: baseUnitsSchema.nullable(),
  maxRedeemRaw: baseUnitsSchema.nullable(),
  valueUsd: decimalStringSchema.nullable(),
  observedAt: nullableIsoDateTime,
  reason: vocabulary.nullable()
});

type ServedWithdrawable = z.infer<typeof servedWithdrawableSchema>;

/** A served figure that could not be read: unmeasured, said why. */
const UNREAD_WITHDRAWABLE: ServedWithdrawable = {
  maxWithdrawRaw: null,
  maxRedeemRaw: null,
  valueUsd: null,
  observedAt: null,
  reason: 'unread'
};

/**
 * One way a vault's money gets out. Only what the app words itself is
 * kept: the served `exitPath` and `venue` are prose and display text, and
 * the screen never prints served text. Kinds, executors and protocol ids
 * are open vocabularies.
 */
const exitRouteSchema = z.object({
  kind: vocabulary,
  protocolId: vocabulary.nullable(),
  executedBy: vocabulary,
  instantWithdrawal: z.boolean().nullable(),
  withdrawalQueueSeconds: z.number().nonnegative().finite().nullable(),
  reason: vocabulary.nullable()
});

export type LiquidityExitRoute = z.infer<typeof exitRouteSchema>;

const liquidityPositionSchema = z.object({
  vaultAddress: evmAddressSchema,
  name: optionalBoundedText,
  valueUsd: decimalStringSchema.nullable(),
  weightBps: exposureWeightBps.nullable(),
  status: liquidityPositionStatus,
  typicalDays: nonNegativeInteger.nullable(),
  worstCaseDays: nonNegativeInteger.nullable(),
  exitPaths: z.array(boundedText).max(1_000),
  assets: z.array(liquidityAssetSchema).max(1_000),
  // API 1.155.0. Undefined: the backend does not serve it yet.
  withdrawableNow: servedWithdrawableSchema
    .optional()
    .catch(unreadAs('withdrawableNow', UNREAD_WITHDRAWABLE)),
  // 'requires_strategist_unwind' | 'exit_cost' |
  // 'withdrawable_now_unmeasured' | 'unpriced' today; open.
  uncoveredReason: vocabulary
    .nullable()
    .optional()
    .catch(unreadAs('uncoveredReason', null)),
  // Null: served but not readable as a list, or not one served row could be
  // read; an empty list would claim the vault has no route.
  routes: tolerantRows(exitRouteSchema, {
    label: 'liquidity exit routes',
    max: 100,
    rejectWhenNoneSurvive: true
  })
    .nullable()
    .optional()
    .catch(unreadAs('routes', null)),
  routesReason: vocabulary
    .nullable()
    .optional()
    .catch(unreadAs('routesReason', null))
});

/** The warnings whose context this app reads, and the keys it reads. */
const WARNING_CONTEXT_CODES = new Set([
  'NO_EXIT_DATA',
  'NOT_WITHDRAWABLE_NOW',
  'WITHDRAWABLE_NOW_UNMEASURED'
]);

/**
 * Why one vault a warning names was not measured (API 1.161.0, on
 * WITHDRAWABLE_NOW_UNMEASURED): 'read_failed', 'share_balance_mismatch' or
 * 'usd_conversion_failed' today, an open vocabulary.
 */
const warningCauseSchema = z.object({
  vault: evmAddressSchema,
  cause: vocabulary
});

const warningContextSchema = z.object({
  valueUsd: decimalStringSchema.optional(),
  // NO_EXIT_DATA: `uncoveredValueUsd` on 1.152, `slicesValueUsd` on 1.155.
  slicesValueUsd: decimalStringSchema.optional(),
  uncoveredValueUsd: decimalStringSchema.optional(),
  vaults: z.array(evmAddressSchema).max(1_000).optional(),
  // API 1.161.0, beside `vaults`. Absent before it, and null is not served:
  // both silent. Read on its own: a cause that does not read is dropped and
  // reported, and a list that does not read is reported and read as not
  // served, never costing the vaults.
  causes: tolerantRows(warningCauseSchema, {
    label: 'liquidity warning causes',
    max: 1_000
  })
    .nullish()
    .catch(
      reportFallback(
        'investor-analytics',
        'investor.liquidity.warnings.causes',
        undefined
      )
    )
});

/** One vault's cause, its address lowercase. */
interface LiquidityWarningCause {
  vault: string;
  cause: string;
}

interface WarningFigures {
  /** The value the note is about, when it names one. */
  valueUsd: string | null;
  /** The vaults it is about, lowercase, when it names them. */
  vaults: string[] | null;
  /** Why each vault was not measured, when the warning says. */
  causes: LiquidityWarningCause[] | null;
}

const NO_FIGURES: WarningFigures = {
  valueUsd: null,
  vaults: null,
  causes: null
};

function readWarningContext(
  code: string,
  context: Record<string, unknown> | undefined
): WarningFigures {
  if (context == null || !WARNING_CONTEXT_CODES.has(code)) return NO_FIGURES;
  const parsed = warningContextSchema.safeParse(context);
  if (!parsed.success) {
    return reportFallback(
      'investor-analytics',
      'investor.liquidity.warnings.context',
      NO_FIGURES
    )({ error: parsed.error });
  }
  const read = parsed.data;
  return {
    valueUsd:
      read.valueUsd ?? read.slicesValueUsd ?? read.uncoveredValueUsd ?? null,
    vaults: read.vaults?.map((vault) => vault.toLowerCase()) ?? null,
    causes:
      read.causes?.map(({ vault, cause }) => ({
        vault: vault.toLowerCase(),
        cause
      })) ?? null
  };
}

/**
 * Every liquidity warning code the app words (rowa-backend
 * `INVESTOR_LIQUIDITY_WARNING_CODES`, API 1.155.0). Another code still
 * parses and reads as a generic note, and is reported once by its code, so
 * a code the backend adds is worded before anyone has to guess at it.
 */
const KNOWN_LIQUIDITY_WARNING_CODES = new Set([
  'NO_EXIT_DATA',
  'ALLOCATION_FALLBACK',
  'ALLOCATION_CONFIG_DERIVED',
  'ESTIMATE_ONLY_BOUNDS',
  'UNPRICED_POSITION',
  'STALE_ALLOCATION_DATA',
  'NOT_WITHDRAWABLE_NOW',
  'WITHDRAWABLE_NOW_UNMEASURED'
]);

/** A code shaped like the backend's own; anything else is not echoed. */
const WARNING_CODE_SHAPE = /^[A-Z][A-Z0-9_]{0,63}$/;

function reportUnknownWarningCode(code: string): void {
  if (KNOWN_LIQUIDITY_WARNING_CODES.has(code)) return;
  // The code is the whole report: never the served message or context,
  // and a served string that is not a code is reported under one name.
  reportFallback(
    'investor-analytics',
    `investor.liquidity.warnings.code.${WARNING_CODE_SHAPE.test(code) ? code : 'malformed'}`,
    null
  )({ error: new Error('Unknown liquidity warning code') });
}

const liquidityWarningSchema = z
  .object({
    // Bounded string, not an enum: a new backend warning code must never
    // reject the whole response (same tolerance rule as the top level).
    code: z.string().max(128),
    // Never shown (the notes speak from the code): cut, never refused.
    message: servedMessageSchema,
    context: z.record(z.string(), z.unknown()).optional()
  })
  .transform((warning) => {
    reportUnknownWarningCode(warning.code);
    return {
      ...warning,
      ...readWarningContext(warning.code, warning.context)
    };
  });

const liquidityProvenanceSchema = z.object({
  metricId: boundedText,
  formulaVersion: boundedText
});

export const investorLiquidityResponseSchema = investorScopeSchema
  .extend({
    asOf: isoDateTime,
    dataAsOf: nullableIsoDateTime,
    populationScope: z.string().max(64).optional(),
    totalPositions: nonNegativeInteger.optional(),
    // 'route-unwind-projection' (to 1.152) or 'vault-withdrawable-now'
    // (1.155). A new basis still parses (it is not a reason to refuse the
    // figures), but only a known one may be described as such: see
    // `isRouteUnwindProjection` and `isWithdrawableNowBasis`.
    basis: vocabulary,
    totalValueUsd: decimalStringSchema,
    // Null = unmeasurable, not zero (API v1.57.0).
    coverageBps: basisPoints.nullable(),
    coveredValueUsd: decimalStringSchema,
    uncoveredValueUsd: decimalStringSchema,
    // Null (1.155): not measured, because a priced vault could not be read
    // or nothing is priced. Never zero; a measured "0.00" is a zero.
    immediateValueUsd: z.object({
      typical: decimalStringSchema.nullable(),
      worstCase: decimalStringSchema.nullable()
    }),
    daysToFullExit: z.object({
      typicalDays: nonNegativeInteger.nullable(),
      worstCaseDays: nonNegativeInteger.nullable()
    }),
    horizon: liquidityHorizonSchema,
    positions: z.array(liquidityPositionSchema).max(100),
    warnings: z.array(liquidityWarningSchema).max(1_000),
    provenance: z.array(liquidityProvenanceSchema).max(100)
  })
  .transform((liquidity) =>
    unmeasuredWithoutPricedPositions(
      noUsdForUnpricedPositions(
        withdrawableNowRowsComplete(legacyT0Measured(liquidity))
      )
    )
  );

/**
 * On the withdrawable now basis every position carries `withdrawableNow`,
 * `uncoveredReason`, `routes` and `routesReason` (API 1.155.0: "always
 * present on every position"). One missing is a contract break: reported
 * once by field, and read as unread (no figure, no route), so the row
 * reads like any other the screen could not measure. The basis decides
 * what a row means, never which fields one row happens to carry.
 */
function withdrawableNowRowsComplete<
  T extends {
    basis: string;
    positions: {
      withdrawableNow?: ServedWithdrawable;
      uncoveredReason?: string | null;
      routes?: LiquidityExitRoute[] | null;
      routesReason?: string | null;
    }[];
  }
>(liquidity: T): T {
  if (!isWithdrawableNowBasis(liquidity.basis)) return liquidity;
  const missing = (
    field: 'withdrawableNow' | 'uncoveredReason' | 'routes' | 'routesReason'
  ) => liquidity.positions.some((position) => position[field] === undefined);
  const fields = (
    ['withdrawableNow', 'uncoveredReason', 'routes', 'routesReason'] as const
  ).filter(missing);
  if (fields.length === 0) return liquidity;
  for (const field of fields) {
    reportBrokenField('investor liquidity', `positions.${field}`);
  }
  return {
    ...liquidity,
    positions: liquidity.positions.map((position) => ({
      ...position,
      withdrawableNow: position.withdrawableNow ?? UNREAD_WITHDRAWABLE,
      uncoveredReason:
        position.uncoveredReason === undefined
          ? null
          : position.uncoveredReason,
      routes: position.routes === undefined ? null : position.routes,
      routesReason:
        position.routesReason === undefined ? null : position.routesReason
    }))
  };
}

/**
 * T+0 may be null only on the withdrawable now basis (API 1.155.0). On
 * the route projection (1.152 and earlier) it is a required figure: a null
 * there is a malformed field, reported once, and the response reads as
 * unmeasured (no coverage, so no tier, card or score is drawn from it).
 */
function legacyT0Measured<
  T extends {
    basis: string;
    coverageBps: number | null;
    immediateValueUsd: { typical: string | null; worstCase: string | null };
  }
>(liquidity: T): T {
  if (
    isWithdrawableNowBasis(liquidity.basis) ||
    (liquidity.immediateValueUsd.typical != null &&
      liquidity.immediateValueUsd.worstCase != null)
  ) {
    return liquidity;
  }
  reportBrokenField('investor liquidity route projection', 'immediateValueUsd');
  return {
    ...liquidity,
    coverageBps: null,
    immediateValueUsd: { typical: null, worstCase: null }
  };
}

/**
 * An unpriced position is out of every USD sum (API 1.155.0). The backend
 * can still serve its USD withdrawable when only its NAV is missing (the FX
 * is present): that is served by design, not a break, so it is set aside
 * here silently, and the vault is read on chain in its own token like
 * every other unpriced vault.
 */
function noUsdForUnpricedPositions<
  T extends {
    positions: {
      valueUsd: string | null;
      withdrawableNow?: { valueUsd: string | null; reason: string | null };
    }[];
  }
>(liquidity: T): T {
  const usdOnUnpriced = liquidity.positions.some(
    (position) =>
      position.valueUsd == null && position.withdrawableNow?.valueUsd != null
  );
  if (!usdOnUnpriced) return liquidity;
  return {
    ...liquidity,
    positions: liquidity.positions.map((position) =>
      position.valueUsd == null && position.withdrawableNow?.valueUsd != null
        ? {
            ...position,
            withdrawableNow: {
              ...position.withdrawableNow,
              valueUsd: null,
              reason: position.withdrawableNow.reason ?? 'unpriced'
            }
          }
        : position
    )
  };
}

/**
 * On the withdrawable now basis (API 1.155.0) T+0 sums the priced
 * positions, so with none of them it is null by contract. A served figure
 * there ("0.00") would read as "nothing can be withdrawn" when nothing was
 * measured: it is read as null, and the break is reported once by field.
 */
function unmeasuredWithoutPricedPositions<
  T extends {
    basis: string;
    immediateValueUsd: { typical: string | null; worstCase: string | null };
    positions: { valueUsd: string | null }[];
  }
>(liquidity: T): T {
  if (
    !isWithdrawableNowBasis(liquidity.basis) ||
    (liquidity.immediateValueUsd.typical == null &&
      liquidity.immediateValueUsd.worstCase == null) ||
    liquidity.positions.some((position) => position.valueUsd != null)
  ) {
    return liquidity;
  }
  reportBrokenField('investor liquidity', 'immediateValueUsd');
  return {
    ...liquidity,
    immediateValueUsd: { typical: null, worstCase: null }
  };
}

export type InvestorLiquidity = z.infer<typeof investorLiquidityResponseSchema>;

/** The one liquidity basis this app knows how to describe. */
const ROUTE_UNWIND_PROJECTION = 'route-unwind-projection';

/**
 * Whether the served basis is the exit route projection. Copy that explains
 * how the figures were computed may only say so when this is true; any
 * other basis is one ROWA has not described yet.
 */
export function isRouteUnwindProjection(basis: string): boolean {
  return basis === ROUTE_UNWIND_PROJECTION;
}

const VAULT_WITHDRAWABLE_NOW = 'vault-withdrawable-now';

/**
 * Whether the served figures are what each vault would pay out now (API
 * 1.155.0): T+0 is the vaults' own `maxWithdraw`, the horizon is flat, and
 * value above it has no timing at all rather than a later tier.
 */
export function isWithdrawableNowBasis(basis: string): boolean {
  return basis === VAULT_WITHDRAWABLE_NOW;
}

/**
 * Whether the served liquidity carries any exit evidence at all. With no
 * verified route behind any position (coverage zero, or not measurable)
 * or every tier empty, the tier figures are not a reading of zero: nothing
 * was measured, and nothing may read as "$0.00 available" or "Live".
 *
 * On the withdrawable now basis the evidence is the vaults' own answer: a
 * served T+0 is measured, zero included, and a null one is not.
 */
export function hasExitEvidence(liquidity: InvestorLiquidity): boolean {
  if (isWithdrawableNowBasis(liquidity.basis)) {
    return liquidity.immediateValueUsd.typical != null;
  }
  if (liquidity.coverageBps == null || liquidity.coverageBps === 0) {
    return false;
  }
  // The horizon is cumulative and monotone, typical never under the worst
  // case (both refined at parse on the basis points, which are read here):
  // its last tier holds the most any tier does, so an empty last tier
  // means every tier is empty.
  const last = liquidity.horizon[liquidity.horizon.length - 1];
  return last != null && last.typical.cumulativeBps > 0;
}
